import { apiFetch } from './client';

// Club-coach goals — mirrors internal/services/coach (docs/ai-club-coach-plan.md
// §3). The backend returns each goal's key + optional target, plus the full
// catalog to pick from; the SPA owns the display metadata (title, description)
// keyed by `key` under the `coach.goals.catalog.<key>` i18n namespace.

export type CoachGoalUnit = 'percent' | 'count' | 'money_minor' | 'days';

export interface CoachGoal {
  goalKey: string;
  targetValue?: number;
  targetUnit?: CoachGoalUnit;
  horizon?: string;
  weight?: number;
}

export interface CoachCatalogEntry {
  key: string;
  suggestedUnit?: CoachGoalUnit;
  relevantChecks: string[];
}

export interface CoachGoalsResponse {
  version: number;
  goals: CoachGoal[];
  catalog: CoachCatalogEntry[];
}

// Min/max mirror internal/services/coach.MinGoals / MaxGoals. Kept in sync by
// hand (the backend is the enforcing authority; these only drive UX hints).
// The editor treats MAX as the number of "focus" goals: the top-N of a single
// ranked list are the club's goals, the rest sit below the cutoff unchosen.
export const MIN_COACH_GOALS = 1;
export const MAX_COACH_GOALS = 3;

export async function getCoachGoals(
  token: string | null,
  slug: string,
): Promise<CoachGoalsResponse> {
  return apiFetch<CoachGoalsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/coach/goals`,
    { method: 'GET', token },
  );
}

export async function setCoachGoals(
  token: string | null,
  slug: string,
  goals: CoachGoal[],
): Promise<CoachGoalsResponse> {
  return apiFetch<CoachGoalsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/coach/goals`,
    { method: 'PUT', token, body: { goals } },
  );
}

// Dashboard — the goal-ranked coach start page (docs/ai-club-coach-plan.md §4).
// Every card is an existing proactive-assistant suggestion; to act, deep-link
// to /orgs/<slug>/assistant/<suggestionId>.

export interface CoachCard {
  suggestionId: string;
  checkKey?: string;
  goalKey?: string;
  title: string;
  summary: string;
  sourceLabel?: string;
  rowCount: number;
  tone: 'default' | 'destructive';
}

export interface CoachGoalBenchmark {
  key: string;
  unit: CoachGoalUnit;
  current?: number;
  prior?: number;
  direction: 'up' | 'down' | 'flat' | 'none';
  // sentiment folds direction with the metric's polarity — colour by this, not
  // direction (a falling drop-out rate is "improved", not "down = bad").
  sentiment: 'improved' | 'worsened' | 'flat' | 'none';
}

// status is the honest per-goal state — colour the chip by this. not_measured
// means the goal has no live measurement yet (its starter tells you how to turn
// it on), so it never masquerades as an "on track" checkmark.
export type CoachGoalStatus = 'risk' | 'attention' | 'on_track' | 'not_measured';

export interface CoachGoalView {
  goalKey: string;
  weight: number;
  targetValue?: number;
  targetUnit?: CoachGoalUnit;
  signalCount: number;
  hasRisk: boolean;
  status: CoachGoalStatus;
  benchmark?: CoachGoalBenchmark;
}

// A proactive "get started" card for a goal whose measurement isn't live yet.
// Unlike a CoachCard it has no suggestionId — its CTA navigates to `route`
// (relative to the org, e.g. "pulse" → /orgs/<slug>/pulse).
export interface CoachStarterCard {
  goalKey: string;
  key: string;
  route: string;
}

export interface CoachDashboard {
  hasGoals: boolean;
  goals: CoachGoalView[];
  cards: CoachCard[];
  risks: CoachCard[];
  thisWeek: CoachCard[];
  starters: CoachStarterCard[];
}

export async function getCoachDashboard(
  token: string | null,
  slug: string,
): Promise<CoachDashboard> {
  return apiFetch<CoachDashboard>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/coach/dashboard`,
    { method: 'GET', token },
  );
}

// Club numbers — the board panel (paying members + leaders, money in this
// month, members still unbilled, this month's activity confirmation). Nullable
// figures mean "can't be computed yet" and must render as an explicit
// unavailable state, never as 0: a club that hasn't flagged its membership-fee
// invoice type has no definition of "paid", and showing 0 paying members there
// would be alarming and wrong.
export interface CoachClubNumbers {
  payingMembers: number | null;
  rosterMembers: number;
  leaders: number;
  collectedThisMonthMinor: number;
  currency: string;
  notNoticed: number | null;
  activitiesConfirmed: number;
  activitiesUnconfirmed: number;
  // First instant of the month every "this month" figure covers (RFC3339), so
  // the panel labels itself from the server's clock rather than the browser's.
  monthStart: string;
}

export async function getCoachClubNumbers(
  token: string | null,
  slug: string,
): Promise<CoachClubNumbers> {
  return apiFetch<CoachClubNumbers>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/coach/club-numbers`,
    { method: 'GET', token },
  );
}
